import { useMemo, useRef } from 'react';
import { useReactiveVar } from '@apollo/client';

import { useMyCareerVacanciesQuery } from 'core/graphql';
import { authState } from 'core/state';
import { UserRole, ISelectOptions } from 'context';

import { ICareerVacancy, collectDirectionOptions, transformCareerVacancies } from './transform';

/** Server-side filter set for the feed; empty strings mean "no filter". */
export interface ICareerVacancyFilters {
  directionId?: string;
  skillId?: string;
  workMode?: string;
  seniority?: string;
  search?: string;
  /** Сузить ленту до сохранённого этим участником. */
  savedOnly?: boolean;
}

interface IUseCareerVacancies {
  vacancies: ICareerVacancy[];
  /**
   * Direction filter options, accumulated across fetches (there is no LMS
   * directions query — spec 0018 §4). A filtered fetch never drops a
   * previously-seen direction, so the selected option is always present.
   */
  directionOptions: ISelectOptions[];
  loading: boolean;
  error: string;
  refetch: () => void;
}

/** Blanks collapse to `undefined` so the server drops the arg instead of matching on ''. */
const clean = (value?: string): string | undefined => value?.trim() || undefined;

/**
 * The member's vacancy feed with server-side filtering.
 *
 * `[]` for a non-member — the backend gates the same way as `careerLessons`,
 * returning an empty list rather than an error. Skipped for any non-Student
 * role (and the demo user), mirroring useCareerLessons: every career resolver
 * rejects a non-Student LMS token (spec 0018 §3.3), so running it would only
 * produce a guaranteed PermissionDenied before the route guard bounces the
 * visitor.
 *
 * `cache-and-network` keeps the feed fresh and lets an interaction mutation's
 * normalized-cache write (vacancy { id myInteraction }) surface immediately.
 */
export const useCareerVacancies = (filters: ICareerVacancyFilters): IUseCareerVacancies => {
  const { active_role, isDemoUser } = useReactiveVar(authState);

  const skip = active_role !== UserRole.STUDENT || Boolean(isDemoUser);

  const { data, loading, error, refetch } = useMyCareerVacanciesQuery({
    variables: {
      directionId: clean(filters.directionId),
      skillId: clean(filters.skillId),
      workMode: clean(filters.workMode),
      seniority: clean(filters.seniority),
      search: clean(filters.search),
      // `undefined`, а не `false`: сервер тогда не получает аргумент вовсе, и
      // ключ кэша совпадает с обычной лентой — переключение фильтра туда-обратно
      // не порождает лишнего сетевого запроса.
      savedOnly: filters.savedOnly ? true : undefined,
    },
    fetchPolicy: 'cache-and-network',
    skip,
  });

  const vacancies = useMemo(() => transformCareerVacancies(data), [data]);

  // Accumulate direction options so a filtered fetch never shrinks the dropdown.
  const optionsRef = useRef<ISelectOptions[]>([]);
  const directionOptions = useMemo(() => {
    optionsRef.current = collectDirectionOptions(vacancies, optionsRef.current);
    return optionsRef.current;
  }, [vacancies]);

  return {
    vacancies,
    directionOptions,
    loading,
    error: error?.message || '',
    refetch,
  };
};
